import ExcelJS from 'exceljs'

export const TASK_INFO_HEADERS = ['车牌号', '开始时间', '结束时间', '始发地', '目的地', '始发地坐标', '目的地坐标', '装货地', '卸货地']
/** 任务信息表头行 + 数据行 + 空行，明细表头从下一行开始 */
export const TASK_INFO_BANNER_ROW_COUNT = 3

const DIRECTION_DEGREES = { 北: 0, 东北: 45, 东: 90, 东南: 135, 南: 180, 西南: 225, 西: 270, 西北: 315 }

function pad(value) {
  return String(value).padStart(2, '0')
}

export function normalizeTrajectoryDateTime(value) {
  if (value == null || value === '') return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())} ${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`
  }
  const text = String(value).trim().replace(/[/.]/g, '-').replace('T', ' ')
  const match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/)
  if (!match) return text
  return `${match[1]}-${pad(match[2])}-${pad(match[3])} ${pad(match[4] || 0)}:${pad(match[5] || 0)}:${pad(match[6] || 0)}`
}

export function normalizeTrajectoryPlate(value) {
  return String(value == null ? '' : value).trim().replace(/_\d$/, '')
}

function metadataValues(metadata = {}) {
  return [
    normalizeTrajectoryPlate(metadata.vclNs || metadata.vehicleNo || metadata.vclN),
    normalizeTrajectoryDateTime(metadata.qryBtmStr || metadata.qryBtm || metadata.startTime),
    normalizeTrajectoryDateTime(metadata.qryEtmStr || metadata.qryEtm || metadata.endTime),
    metadata.startAddress || metadata.originAddress || metadata.origin || '',
    metadata.endAddress || metadata.destinationAddress || metadata.destination || '',
    metadata.startLonlat || metadata.originCoord || '',
    metadata.endLonlat || metadata.destCoord || '',
    metadata.loadAddress || metadata.loadingAddress || '',
    metadata.unloadAddress || metadata.unloadingAddress || ''
  ].map((value) => value == null ? '' : String(value).trim())
}

function styleTaskInfoHeaderRow(row) {
  row.font = { bold: true, color: { argb: 'FFFFFF' } }
  row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } }
  row.alignment = { horizontal: 'center', vertical: 'middle' }
  row.height = 22
}

/**
 * 在工作表顶部写入任务信息（表头+数据+空行），供停靠点/轨迹明细/轨迹地图共用。
 * @returns {number} 明细表头应写入的行号（1-based）
 */
export function writeTrajectoryTaskInfoBanner(sheet, metadata = {}) {
  if (!sheet) return 1
  const headerRow = sheet.addRow(TASK_INFO_HEADERS)
  styleTaskInfoHeaderRow(headerRow)
  const valueRow = sheet.addRow(metadataValues(metadata))
  valueRow.height = 22
  sheet.addRow([])
  TASK_INFO_HEADERS.forEach((_, index) => {
    const column = sheet.getColumn(index + 1)
    if (!column.width || column.width < 18) column.width = 24
  })
  return TASK_INFO_BANNER_ROW_COUNT + 1
}

/** @deprecated 已改为各明细表顶部横幅；保留空实现避免旧调用报错 */
export function addTrajectoryTaskInfoSheet() {
  return null
}

/**
 * 写入明细列头并套用表格样式（须先 writeTrajectoryTaskInfoBanner）。
 * @param {import('exceljs').Worksheet} sheet
 * @param {Array<{ header: string, key?: string, width?: number }|string>} columns
 * @returns {number} 表头行号
 */
export function writeTrajectoryDetailTableHeader(sheet, columns = []) {
  const headers = columns.map((col) => (typeof col === 'string' ? col : col.header))
  const widths = columns.map((col) => (typeof col === 'string' ? undefined : col.width))
  const keys = columns.map((col) => (typeof col === 'string' ? undefined : col.key))
  if (keys.some(Boolean)) {
    sheet.columns = keys.map((key, index) => ({
      key: key || undefined,
      width: widths[index] || sheet.getColumn(index + 1).width || 16
    }))
  } else {
    widths.forEach((width, index) => {
      if (width) sheet.getColumn(index + 1).width = width
    })
  }
  const headerRow = sheet.addRow(headers)
  styleTaskInfoHeaderRow(headerRow)
  headerRow.height = 25
  return headerRow.number
}

export function buildLocalTrajectoryMatchKey(pageType, metadata = {}) {
  const type = String(pageType || '').trim()
  const values = metadataValues(metadata)
  return `${type}|${values[0]}|${values[1]}|${values[2]}`
}

function coordinateText(point) {
  if (!point || !Number.isFinite(Number(point.lon)) || !Number.isFinite(Number(point.lat))) return ''
  return `${Number(point.lon)},${Number(point.lat)}`
}

export function buildTrajectoryTaskMetadata(vm = {}) {
  const override = vm.displayOverride || vm.trajectoryDisplayOverride || {}
  const startOverride = override && typeof override === 'object' ? override.start || {} : {}
  const endOverride = override && typeof override === 'object' ? override.end || {} : {}
  const businessAddress = (side) => {
    const o = side && side.businessAddress != null ? String(side.businessAddress).trim() : ''
    return o
  }
  const loadAddress = businessAddress(startOverride)
  const unloadAddress = businessAddress(endOverride)

  // 始发地/目的地取轨迹真实起终点地址；装卸货地只作独立列，不覆盖终点地址
  const cleanAddress = (value) => {
    const v = String(value == null ? '' : value).trim()
    if (!v || v === '鼠标移上获取位置...' || v === '暂无数据' || v === '获取地址中...') return ''
    return v
  }
  const endpointAddress = (info, display, hasOverride) => {
    const fromInfo = cleanAddress(info && info.location)
    if (fromInfo) return fromInfo
    if (hasOverride) return ''
    return cleanAddress(display)
  }

  return {
    vclNs: vm.plateForTripHeader || vm.tripHeaderOverride || vm.licensePlate || vm.plateFromQuery || '',
    qryBtmStr: normalizeTrajectoryDateTime(override.startTime || (vm.tripStartInfo && vm.tripStartInfo.time)),
    qryEtmStr: normalizeTrajectoryDateTime(override.endTime || (vm.tripEndInfo && vm.tripEndInfo.time)),
    startAddress: endpointAddress(vm.tripStartInfo, vm.tripStartLocationDisplay, !!loadAddress),
    endAddress: endpointAddress(vm.tripEndInfo, vm.tripEndLocationDisplay, !!unloadAddress),
    startLonlat: override.startLonlat || override.originCoord || coordinateText(vm.tripStartInfo),
    endLonlat: override.endLonlat || override.destCoord || coordinateText(vm.tripEndInfo),
    loadAddress,
    unloadAddress
  }
}

function cellText(cell) {
  if (!cell) return ''
  const value = cell.value
  if (value == null) return ''
  if (value instanceof Date) return normalizeTrajectoryDateTime(value)
  if (typeof value === 'object' && value.text != null) return String(value.text).trim()
  if (typeof value === 'object' && value.result != null) return String(value.result).trim()
  return String(value).trim()
}

function parseCoordinate(value) {
  const match = String(value || '').match(/^\s*(-?\d+(?:\.\d+)?)\s*[,，]\s*(-?\d+(?:\.\d+)?)\s*$/)
  if (!match) return null
  const lon = Number(match[1])
  const lat = Number(match[2])
  if (!Number.isFinite(lon) || !Number.isFinite(lat) || Math.abs(lon) > 180 || Math.abs(lat) > 90) return null
  return { lon, lat }
}

function parseDirection(value) {
  const text = String(value == null ? '' : value).trim()
  if (Object.prototype.hasOwnProperty.call(DIRECTION_DEGREES, text)) return DIRECTION_DEGREES[text]
  const number = Number(text)
  return Number.isFinite(number) ? number : 0
}

function readTaskMetadataFromHeaderRow(sheet, headerRowNumber) {
  const infoHeaders = new Map()
  sheet.getRow(headerRowNumber).eachCell((cell, col) => infoHeaders.set(cellText(cell), col))
  if (!infoHeaders.has('车牌号') || !infoHeaders.has('开始时间')) return null
  const infoRow = sheet.getRow(headerRowNumber + 1)
  const readInfo = (header) => cellText(infoRow.getCell(infoHeaders.get(header) || 0))
  return {
    vclNs: normalizeTrajectoryPlate(readInfo('车牌号')),
    qryBtmStr: normalizeTrajectoryDateTime(readInfo('开始时间')),
    qryEtmStr: normalizeTrajectoryDateTime(readInfo('结束时间')),
    startAddress: readInfo('始发地'),
    endAddress: readInfo('目的地'),
    startLonlat: readInfo('始发地坐标'),
    endLonlat: readInfo('目的地坐标')
  }
}

function findTaskMetadata(workbook, trackSheet) {
  const legacy = workbook.getWorksheet('任务信息')
  if (legacy) {
    const metadata = readTaskMetadataFromHeaderRow(legacy, 1)
    if (metadata) return metadata
  }
  const candidates = [trackSheet, workbook.getWorksheet('停靠点明细'), workbook.getWorksheet('轨迹地图')]
  for (let i = 0; i < candidates.length; i += 1) {
    const sheet = candidates[i]
    if (!sheet) continue
    const maxScan = Math.min(6, sheet.rowCount || 0)
    for (let rowNumber = 1; rowNumber <= maxScan; rowNumber += 1) {
      const metadata = readTaskMetadataFromHeaderRow(sheet, rowNumber)
      if (metadata) return metadata
    }
  }
  return null
}

function findTrackTableHeader(trackSheet) {
  const maxScan = Math.min(12, trackSheet.rowCount || 0)
  for (let rowNumber = 1; rowNumber <= maxScan; rowNumber += 1) {
    const headers = new Map()
    trackSheet.getRow(rowNumber).eachCell((cell, col) => headers.set(cellText(cell), col))
    if (headers.has('时间') && headers.has('坐标')) {
      return { headers, headerRow: rowNumber }
    }
  }
  return null
}

export async function parseTrajectoryWorkbook(arrayBuffer) {
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(await Promise.resolve(arrayBuffer))
  const trackSheet = workbook.getWorksheet('轨迹明细')
  if (!trackSheet) throw new Error('缺少“轨迹明细”工作表')

  const metadata = findTaskMetadata(workbook, trackSheet)
  if (!metadata) throw new Error('缺少任务信息（明细表顶部或“任务信息”工作表）')
  if (!metadata.vclNs || !metadata.qryBtmStr || !metadata.qryEtmStr) {
    throw new Error('任务信息缺少车牌号、开始时间或结束时间')
  }

  const table = findTrackTableHeader(trackSheet)
  if (!table) throw new Error('轨迹明细缺少“时间”或“坐标”列')
  const { headers, headerRow } = table
  const trackRows = []
  for (let rowNumber = headerRow + 1; rowNumber <= trackSheet.rowCount; rowNumber++) {
    const row = trackSheet.getRow(rowNumber)
    const coordinate = cellText(row.getCell(headers.get('坐标')))
    const parsed = parseCoordinate(coordinate)
    if (!parsed) continue
    trackRows.push({
      time: normalizeTrajectoryDateTime(cellText(row.getCell(headers.get('时间')))),
      location: cellText(row.getCell(headers.get('位置'))),
      coordinate,
      lon: parsed.lon,
      lat: parsed.lat,
      agl: parseDirection(cellText(row.getCell(headers.get('方向')))),
      mil: Number(cellText(row.getCell(headers.get('上报里程')))) || 0,
      hgt: Number(cellText(row.getCell(headers.get('海拔')))) || 0,
      speed: Number(cellText(row.getCell(headers.get('速度')))) || 0
    })
  }
  if (trackRows.length < 2) throw new Error('轨迹明细至少包含两个有效坐标点')
  return { metadata, trackRows }
}
